import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdminCategory } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '.';
import { productServiceAdminCategoriesClient } from '../api-client/product-service-client';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { resolveAxiosErrorResponse } from '../utils/errors';

type CategoriesState = EntriesState<AdminCategory>;

export const initialAdminCategoriesState: CategoriesState = initialEntriesState;

const adminCategoriesAdapter = createEntityAdapter<AdminCategory>();

export const fetchAdminCategories = createAsyncThunk('adminCategories/fetchAdminCategoriesStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await productServiceAdminCategoriesClient.getAdminCategories();
    return response.data.categories;
  } catch (error) {
    return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
  }
});

const adminCategories = createSlice({
  name: 'adminCategories',
  initialState: adminCategoriesAdapter.getInitialState(initialAdminCategoriesState),
  reducers: {
    addOneAdminCategory: adminCategoriesAdapter.addOne,
    updateOneAdminCategory(state, action: PayloadAction<AdminCategory>) {
      adminCategoriesAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
    },
    removeOneAdminCategory: adminCategoriesAdapter.removeOne,
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminCategories.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminCategories.fulfilled, (state, action) => {
      if (action.payload) {
        adminCategoriesAdapter.setAll(state, action.payload);
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminCategories.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { addOneAdminCategory, updateOneAdminCategory, removeOneAdminCategory } = adminCategories.actions;

export const { selectAll: selectAllCategories, selectById: selectCategoryById } = adminCategoriesAdapter.getSelectors(
  (state: RootState) => state.adminCategories,
);

export const selectAdminCategoriesLoading = (state: RootState) => state.adminCategories.loading;

export default adminCategories.reducer;
